import { supabase } from '@/integrations/supabase/client';
import { exportToWord, exportToPDF } from './exportService';
import { invokeApi } from '@/lib/apiClient';

interface GenerateResumenParams {
  content?: string;
  topicQuery?: string;
  userQuestion?: string;
  autonomousCommunity?: string;
  examType?: string;
}

interface ResumenResult {
  markdown: string;
  title: string;
}

interface StreamChunk {
  chunk?: string;
  title?: string;
  status?: string;
  message?: string;
  error?: string;
}

/**
 * Generate a structured resumen using VPS with fallback to Supabase Edge Functions.
 */
export async function generateResumen(params: GenerateResumenParams): Promise<ResumenResult> {
  const { data, error } = await invokeApi<ResumenResult>('generate-resumen', {
    body: params,
  });

  if (error || !data) {
    throw error || new Error('No se pudo generar el resumen');
  }

  return data;
}

/**
 * Generate a structured resumen using a streaming VPS endpoint.
 * Includes fallback to non-streaming edge function if VPS fails.
 */
export async function* generateResumenStream(params: GenerateResumenParams): AsyncGenerator<StreamChunk> {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  
  if (!accessToken) throw new Error('No se encontró una sesión activa');

  const { getApiHeaders, API_URL } = await import('@/lib/apiClient');
  
  let response: Response;
  let useFallback = false;

  try {
    response = await fetch(`${API_URL}/generate-resumen-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status >= 500) {
        useFallback = true;
      } else {
        const errData = await response.json().catch(() => ({ error: `Error ${response.status}` }));
        throw new Error(errData.error || 'Error en el servidor');
      }
    }
  } catch (err) {
    console.warn('[resumenService] VPS error, falling back to edge function:', err);
    useFallback = true;
  }

  if (useFallback) {
    console.log('[resumenService] Using edge function fallback');
    const { data, error } = await invokeApi<ResumenResult>('generate-resumen', { body: params });
    if (error || !data) throw error || new Error('Error en el fallback de generación');
    
    yield { status: 'generating', message: 'Usando servidor de respaldo...' };
    yield { chunk: data.markdown, title: data.title, status: 'done' };
    return;
  }

  // Handle VPS Stream
  const reader = response!.body?.getReader();
  if (!reader) throw new Error('No se pudo abrir el canal de datos');

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('data: ')) {
          const dataStr = trimmed.slice(6);
          if (dataStr === '[DONE]') break;
          
          try {
            const data = JSON.parse(dataStr);
            if (data.error) throw new Error(data.error);
            yield data as StreamChunk;
          } catch (e) {
            // Ignore parse errors for malformed chunks
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Generate resumen and download as Word
 */
export async function downloadResumenAsWord(params: GenerateResumenParams, chatMode?: any): Promise<void> {
  const result = await generateResumen(params);
  const filename = sanitizeFilename(result.title);
  await exportToWord(result.markdown, filename, chatMode);
}

/**
 * Generate resumen and download as PDF
 */
export async function downloadResumenAsPDF(params: GenerateResumenParams, chatMode?: any): Promise<void> {
  const result = await generateResumen(params);
  const filename = sanitizeFilename(result.title);
  await exportToPDF(result.markdown, `${filename}.pdf`, chatMode);
}

function sanitizeFilename(title: string): string {
  return title
    .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s-]/g, '')
    .trim()
    .substring(0, 80) || 'Resumen';
}
